import _ from 'lodash'
import {ActionTree} from 'vuex'
import {AuthorizationState} from '@/modules/authorization/store/index'
import {RootState} from '@/store/types'
import * as api from '@/modules/authorization/api'
import {MutationTypes} from '@/modules/authorization/store/mutations';
import Permission from '@/modules/authorization/model/Permission';
import Role from '@/modules/authorization/model/Role';
import {RoleUserAssignationListInput} from '@/modules/authorization/api';
import {CheckAuthorizationResult} from '@/modules/authorization/model/CheckAuthorizationResult';

const mutation: ActionTree<AuthorizationState, RootState> = {
    /**
     * Carga en el estado de la aplicación el árbol completo con todos los
     * permisos registrados en la aplicación
     */
    async loadFullPermissionTree ({commit}) {
        // Obtiene el permiso raíz del sistema, del cual deberían heredar todos los
        // demás permisos
        const permission = await api.getPermission('@')
        commit(MutationTypes.SetPermissionTree, permission)
    },

    /**
     * Crea nuevo permiso y lo inserta en el árbol
     */
    async createNewPermission ({state, commit}, path: string) {
        const permission = Object.assign(new Permission(), {
            path,
            key: '__new_permission',
            title: 'Nuevo permiso',
        })

        commit(MutationTypes.AddPermission, {path, permission})
        commit(MutationTypes.SetCurrentPermission, permission.path)
        commit(MutationTypes.ToggleEditingPermission, true)

        return permission
    },

    /**
     * Resetea el estado del permiso actual en edición
     *
     * @param state
     * @param commit
     */
    resetCurrentPermission ({state, commit}) {
        if (!state.editingPermission) {
            return
        }

        const newPermission = state.permissions.findOccurrences('__new_permission')[0]
        if (newPermission) {
            commit(MutationTypes.DeletePermission, newPermission.path)
        }

        commit(MutationTypes.ResetPermission)
        commit(MutationTypes.ToggleEditingPermission, false)
    },

    /**
     * Guardar permiso
     *
     * Registra los cambios de un permiso en el servidor
     */
    async savePermission ({state, commit}) {
        let permission = state.permission
        if (permission.isNew) {
            permission = await api.createPermission(permission)
            commit(MutationTypes.AddPermission, {path: permission.parentPath, permission })
            commit(MutationTypes.DeletePermission, `${permission.parentPath}/__new_permission`)
        } else {
            // Permission.path mantiene la ruta del permiso original, la ruta actualizada por el usuario
            // si es que ha sido modificada se encuentra en updatedPath
            const oldPath = permission.path
            // El permiso ya se encuentra registrado en la aplicación, se procede a actualizar
            // sus campos
            const updated = await api.updatePermission(permission)
            commit(MutationTypes.DeletePermission, oldPath)
            commit(MutationTypes.AddPermission, {path: updated.parentPath, permission: updated})
            commit(MutationTypes.SetCurrentPermission, updated.path)
        }
    },

    /**
     * Obtiene del servidor la lista completa de permisos
     */
    async loadFullRoleTree ({commit}) {
        const tree = await api.getRole('admin')
        commit(MutationTypes.SetRoleTree, tree)
    },

    /**
     * Crea nuevo permiso y lo inserta en el árbol
     */
    async createNewRole ({state, commit}, path: string) {
        const role = Object.assign(new Role(), {
            path,
            key: '__new_role',
            title: 'Nuevo permiso',
        })

        commit(MutationTypes.AddRole, {path, role})
        commit(MutationTypes.SetCurrentRole, role.path)
        commit(MutationTypes.ToggleEditingPermission, true)

        return role
    },

    /**
     * @param state
     * @param commit
     * @param path
     */
    async loadRole ({state, commit}, path: string) {
        commit(MutationTypes.SetCurrentRole, _.cloneDeep(path))
        commit(MutationTypes.ToggleEditingRole, true)
    },

    /**
     * Guardar rol
     *
     * Registra en el servidor los cambios que el usuario ha realizado sobre el
     * rol actual. Si el rol es nuevo, se creará en la aplicación y si
     * ya existe se actualizará su información
     */
    async saveRole ({state, commit}) {
        let role = state.role
        role = role.isNew
            ? await api.createRole(role)
            : await api.updateRole(role)

        commit(MutationTypes.AddRole, { path: role.parentPath, role })
        commit(MutationTypes.DeleteRole, `${role.parentPath}/__new_role`)
        commit(MutationTypes.SetCurrentRole, role.path)
    },

    /**
     * Carga la lista de usuario
     * @param commit
     * @param payload
     * @param role
     */
    async loadRoleUserAssignationList ({commit}, payload: {role: string, input: RoleUserAssignationListInput}) {
        const { role, input } = payload
        const pagination = await api.getRoleUserAssignationList(role, input)

        commit(MutationTypes.SetRoleUserAssignationList, pagination)
    },

    /**
     * Actualiza la lista de usuarios asignados
     *
     * Acción invocada cuando el usuario solicita guardar las asignaciones desde la pantalla
     * de usuarios asignados en el detalle del rol
     */
    async updateRoleUserAssignationList ({state}) {
        await api.updateRoleUserAssignationList(state.role.path, state.updatedRoleUserAssignationList)
    },

    /**
     * Comprueba si los permisos argumentos se encuentran habilitados para el usuario actual
     * de la aplicación
     *
     * @param state
     * @param commit
     * @param permissions
     */
    async checkAuthorization ({state, commit}, permissions: string[]) {
        const status = new CheckAuthorizationResult()
        const sanitized = permissions.filter(p => !!p)
        // Filtra de la lista de permisos argumentada aquellos que no todavía no han sido
        // obtenidos
        const notFetched = sanitized.filter(p => {
            const g = state.grantedPermissions.find(gp => gp === p)
            const r = state.rejectedPermissions.find(rp => rp.path === p)
            if (!g && !r) {
                return true
            }

            if (g) {
                status.granted.push(g)
            }

            if (r) {
                status.rejected.push(r)
            }

            return false
        })

        // Todos los permisos solicitados por el usuario ya han sido solicitados al servidor
        // no es necesario enviar otra petición
        if (!notFetched.length) {
            return status
        }

        // Solicita al servidor que compruebe si el usuario actual posee los permisos argumentados
        const result = await api.checkAuthorization(notFetched)
        status.merge(result)
        commit(MutationTypes.MergeGrantedPermissions, status.granted)
        commit(MutationTypes.MergeRejectedPermissions, status.rejected)

        return status
    },

    /**
     * Limpia el estado de autorización
     *
     * @param commit
     */
    async clearStatus ({commit}) {
        commit(MutationTypes.SetGrantedPermission, [])
        commit(MutationTypes.SetRejectedPermissions, [])
    },

    /**
     * Intenta eliminar un rol de usuario
     *
     * @param state
     * @param commit
     * @param path
     */
    async deleteRole ({state, commit}, path: string) {
        await api.deleteRole(path)
        commit(MutationTypes.DeleteRole, path)
    },
}

export default mutation

